import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { useAccountSupportTicketsQuery } from '../../../queries/Support/useAccountSupportTicketsQuery';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import { AppDrawerToolbarButton } from '../../../web/components-v2/Drawer/AppDrawer';
import { SupportIcon } from '../../../web/components/svgs/icons/SupportIcon';
import { NewTicketModal } from '../../components/supportShared/NewTicketModal';
import type { NewTicketMode } from '../../components/supportShared/NewTicketModal/newTicketDraft';
import { NewTicketMenu } from '../../components/supportShared/NewTicketMenu';
import { TicketChannelBadge } from '../../components/supportShared/TicketChannelBadge';
import {
	formatRelativeTime,
	getStatusInfo,
	getTicketContactLabel,
	PRIORITY_COLOR,
} from '../AdminSupportInboxPage/supportTicketPresentation';
import { accountSupportTicketsStyles as s } from './accountSupportTicketsStyles';

/** Shared by the drawer panel and its toolbar button so both address the same drawer slot. */
export const SUPPORT_TICKETS_DRAWER_VALUE = 'support-tickets';

/** Badges above this read as "9+" rather than widening the 40px toolbar button. */
const BADGE_COUNT_MAX = 9;

/** Deep link to the support desk's ticket list, pre-filtered to this account. */
function buildAccountSupportTicketsHash(accountId: string, accountName?: string) {
	const supportParams = new URLSearchParams({ filter: 'all', accountId });
	if (accountName) {
		supportParams.set('accountName', accountName);
	}
	return `#/support?${supportParams.toString()}`;
}

/** `view=desk` opens the conversation directly; without it the support page falls back to the table. */
const buildTicketHash = (ticketId: string) => `#/support?${new URLSearchParams({ view: 'desk', ticketId }).toString()}`;

const TicketRow: React.FC<{ ticket: ISupportTicket }> = ({ ticket }) => {
	const status = getStatusInfo(ticket.status);
	const contactLabel = getTicketContactLabel(ticket);
	return (
		<a href={buildTicketHash(ticket.id)} className={css(s.row)} title={`#${ticket.number} · ${ticket.subject}`}>
			<div className={css(s.rowTop)}>
				<span className={css(s.rowNumber)}>#{ticket.number}</span>
				<span className={css(s.rowTime)}>{formatRelativeTime(ticket.lastMessageAt)}</span>
			</div>
			<div className={css(s.rowSubject)}>{ticket.subject}</div>
			<div className={css(s.rowContact)}>{contactLabel}</div>
			<div className={css(s.rowPills)}>
				<Pill variant='tag' color={status.color} size='small' label={status.label} />
				<TicketChannelBadge channel={ticket.channel} compact />
				<Pill variant='label' color={PRIORITY_COLOR[ticket.priority]} size='small' label={ticket.priority} />
			</div>
		</a>
	);
};

/**
 * Total open tickets, which can exceed the fetched page. Both the panel header and the toolbar badge
 * read it so they never disagree.
 */
const getOpenTicketCount = (data: { values: ISupportTicket[]; totalCount?: number } | undefined) =>
	data?.totalCount ?? data?.values.length ?? 0;

const pluralizeTickets = (count: number) => `${count} open ticket${count === 1 ? '' : 's'}`;

/**
 * The drawer's toolbar entry. It runs the same account tickets query as the panel — react-query serves
 * both from one cache entry, so the badge costs no extra request.
 */
export const AccountSupportTicketsDrawerButton: React.FC<{ accountId: string }> = ({ accountId }) => {
	const ticketsQuery = useAccountSupportTicketsQuery(accountId);
	const openCount = getOpenTicketCount(ticketsQuery.data);

	return (
		<AppDrawerToolbarButton
			value={SUPPORT_TICKETS_DRAWER_VALUE}
			tooltip={openCount > 0 ? `Support Tickets — ${pluralizeTickets(openCount)}` : 'Support Tickets'}
		>
			<SupportIcon stroke='currentColor' />
			{openCount > 0 && (
				<span className={css(s.iconBadge)} aria-label={pluralizeTickets(openCount)}>
					{openCount > BADGE_COUNT_MAX ? `${BADGE_COUNT_MAX}+` : openCount}
				</span>
			)}
		</AppDrawerToolbarButton>
	);
};

export interface IAccountSupportTicketsPanelProps {
	accountId: string;
	accountName?: string;
	/** The support-desk deep link keeps the account page's stricter developer-only gate. */
	canOpenSupportDesk: boolean;
}

/**
 * Account-details drawer panel: the account's open support tickets plus the entry point for filing a
 * new one. Replaces the page header's "New Support Ticket" action and "Support Tickets" link.
 */
export const AccountSupportTicketsPanel: React.FC<IAccountSupportTicketsPanelProps> = ({
	accountId,
	accountName,
	canOpenSupportDesk,
}) => {
	const [newTicketMode, setNewTicketMode] = React.useState<NewTicketMode | null>(null);
	const ticketsQuery = useAccountSupportTicketsQuery(accountId);
	const tickets = ticketsQuery.data?.values ?? [];
	const openCount = getOpenTicketCount(ticketsQuery.data);

	return (
		<div className={css(s.panel)}>
			<div className={css(s.header)}>
				<span className={css(s.title)}>
					Support Tickets
					{openCount > 0 && <span className={css(s.count)}>{openCount}</span>}
				</span>
				<NewTicketMenu
					onSelect={setNewTicketMode}
					render={
						<Button variant='primary' size='sm'>
							New Ticket
						</Button>
					}
				/>
			</div>

			{ticketsQuery.isLoading ? (
				<div className={css(s.message)}>Loading tickets…</div>
			) : ticketsQuery.isError ? (
				<div className={css(s.message)}>Couldn't load tickets for this account.</div>
			) : tickets.length === 0 ? (
				<div className={css(s.message)}>No open tickets for this account.</div>
			) : (
				<div className={css(s.list)}>
					{tickets.map(ticket => (
						<TicketRow key={ticket.id} ticket={ticket} />
					))}
				</div>
			)}

			{canOpenSupportDesk && (
				<div className={css(s.footer)}>
					<Button variant='link' size='sm' styles={[s.footerLink]} asChild>
						<a href={buildAccountSupportTicketsHash(accountId, accountName)}>View all in the support desk</a>
					</Button>
				</div>
			)}

			{newTicketMode && (
				<NewTicketModal
					mode={newTicketMode}
					defaultAccount={{ id: accountId, name: accountName }}
					onClose={() => setNewTicketMode(null)}
				/>
			)}
		</div>
	);
};
